import React, { useState, useCallback } from 'react';
import styled from 'styled-components';

// Solitaire — Simple card game recreation
// Classic Klondike solitaire with XP green felt background

const SUITS = ['♠', '♥', '♦', '♣'];
const SUIT_COLORS = { '♠': '#000', '♥': '#c00', '♦': '#c00', '♣': '#000' };
const VALUES = ['A', '2', '3', '4', '5', '6', '7', '8', '9', '10', 'J', 'Q', 'K'];

function createDeck() {
    const deck = [];
    for (const suit of SUITS) {
        for (const value of VALUES) {
            deck.push({ suit, value, faceUp: false });
        }
    }
    // Shuffle
    for (let i = deck.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [deck[i], deck[j]] = [deck[j], deck[i]];
    }
    return deck;
}

function dealGame() {
    const deck = createDeck();
    const tableau = [[], [], [], [], [], [], []];
    let cardIndex = 0;

    for (let col = 0; col < 7; col++) {
        for (let row = 0; row <= col; row++) {
            const card = { ...deck[cardIndex++] };
            if (row === col) card.faceUp = true;
            tableau[col].push(card);
        }
    }

    return {
        tableau,
        stock: deck.slice(cardIndex),
        waste: [],
        foundations: [[], [], [], []],
    };
}

function Solitaire() {
    const [game, setGame] = useState(dealGame);
    const [wins, setWins] = useState(0);

    const drawCard = useCallback(() => {
        setGame(g => {
            if (g.stock.length === 0) {
                return { ...g, stock: [...g.waste].reverse().map(c => ({ ...c, faceUp: false })), waste: [] };
            }
            const card = { ...g.stock[g.stock.length - 1], faceUp: true };
            return {
                ...g,
                stock: g.stock.slice(0, -1),
                waste: [...g.waste, card],
            };
        });
    }, []);

    const newGame = useCallback(() => {
        setGame(dealGame());
    }, []);

    return (
        <Container>
            <MenuBar>
                <MenuItem onClick={newGame}>Game</MenuItem>
                <MenuItem>Help</MenuItem>
                <Score>Score: {wins * 100} | Games: {wins}</Score>
            </MenuBar>
            <GameArea>
                {/* Stock and Waste */}
                <TopRow>
                    <CardSlot onClick={drawCard}>
                        {game.stock.length > 0 ? (
                            <CardBack />
                        ) : (
                            <EmptySlot>↻</EmptySlot>
                        )}
                    </CardSlot>
                    <CardSlot>
                        {game.waste.length > 0 && (
                            <Card
                                suit={game.waste[game.waste.length - 1].suit}
                                value={game.waste[game.waste.length - 1].value}
                            />
                        )}
                    </CardSlot>
                    <Spacer />
                    {game.foundations.map((f, i) => (
                        <CardSlot key={i}>
                            {f.length > 0 ? (
                                <Card suit={f[f.length - 1].suit} value={f[f.length - 1].value} />
                            ) : (
                                <EmptySlot>{SUITS[i]}</EmptySlot>
                            )}
                        </CardSlot>
                    ))}
                </TopRow>

                {/* Tableau */}
                <TableauArea>
                    {game.tableau.map((column, colIdx) => (
                        <TableauColumn key={colIdx}>
                            {column.length === 0 && <EmptySlot />}
                            {column.map((card, cardIdx) => (
                                <TableauCard key={cardIdx} offset={cardIdx}>
                                    {card.faceUp ? (
                                        <Card suit={card.suit} value={card.value} />
                                    ) : (
                                        <CardBack />
                                    )}
                                </TableauCard>
                            ))}
                        </TableauColumn>
                    ))}
                </TableauArea>
            </GameArea>
        </Container>
    );
}

function Card({ suit, value }) {
    return (
        <CardFace color={SUIT_COLORS[suit]}>
            <CardCorner>
                <div>{value}</div>
                <div>{suit}</div>
            </CardCorner>
            <CardCenter>{suit}</CardCenter>
            <CardCorner className="bottom">
                <div>{value}</div>
                <div>{suit}</div>
            </CardCorner>
        </CardFace>
    );
}

const Container = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #008000;
`;

const MenuBar = styled.div`
  display: flex;
  align-items: center;
  background: #ECE9D8;
  padding: 2px 4px;
  border-bottom: 1px solid #ACA899;
  font-family: Tahoma, sans-serif;
  font-size: 11px;
`;

const MenuItem = styled.div`
  padding: 2px 8px;
  cursor: pointer;
  &:hover { background: #316AC5; color: #fff; }
`;

const Score = styled.div`
  margin-left: auto;
  color: #666;
`;

const GameArea = styled.div`
  flex: 1;
  padding: 12px;
  overflow: hidden;
`;

const TopRow = styled.div`
  display: flex;
  gap: 8px;
  margin-bottom: 16px;
`;

const Spacer = styled.div`
  flex: 1;
`;

const CardSlot = styled.div`
  width: 60px;
  height: 84px;
  cursor: pointer;
`;

const EmptySlot = styled.div`
  width: 60px;
  height: 84px;
  border: 2px dashed rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(255, 255, 255, 0.4);
  font-size: 20px;
`;

const CardBack = styled.div`
  width: 60px;
  height: 84px;
  background: linear-gradient(135deg, #1a237e 0%, #283593 50%, #1a237e 100%);
  border: 2px solid #fff;
  border-radius: 4px;
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.3);
  background-image: repeating-linear-gradient(
    45deg,
    transparent,
    transparent 4px,
    rgba(255, 255, 255, 0.1) 4px,
    rgba(255, 255, 255, 0.1) 8px
  );
`;

const CardFace = styled.div`
  width: 60px;
  height: 84px;
  background: #fff;
  border: 1px solid #999;
  border-radius: 4px;
  box-shadow: 1px 1px 3px rgba(0, 0, 0, 0.3);
  color: ${({ color }) => color};
  font-family: 'Georgia', serif;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const CardCorner = styled.div`
  position: absolute;
  top: 2px;
  left: 4px;
  font-size: 10px;
  line-height: 1;
  text-align: center;
  &.bottom {
    top: auto;
    left: auto;
    bottom: 2px;
    right: 4px;
    transform: rotate(180deg);
  }
`;

const CardCenter = styled.div`
  font-size: 24px;
`;

const TableauArea = styled.div`
  display: flex;
  gap: 8px;
`;

const TableauColumn = styled.div`
  width: 60px;
  position: relative;
  min-height: 84px;
`;

const TableauCard = styled.div`
  position: absolute;
  top: ${({ offset }) => offset * 20}px;
`;

export default Solitaire;
